import { useEffect, useRef, useState } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { LANDED_MARK, LANDING_HIGHLIGHT_MS, LANDING_SCROLL } from './landing';

/** What a landing hands its element: the ref to wear, whether it is lit, and the mark. */
export interface Landing<T extends HTMLElement> {
  ref: (el: T | null) => void;
  lit: boolean;
  /** The classes a landed element wears while it is lit ('' otherwise). */
  mark: string;
}

/**
 * Land on the exact item a link names (UX baseline): while the URL's hash is `anchor`, the
 * element wearing `ref` is scrolled to ONCE per arrival, `onLand` runs (a field takes the
 * cursor), and it is marked (`LANDED_MARK`, `data-landed`) for `LANDING_HIGHLIGHT_MS`.
 *
 * ⭐ The element may be drawn AFTER the navigation (once its card's read answers), when the
 * router's own hash scroll has already looked and found nothing. So the ref is a callback that
 * keeps the element in state: the landing runs the moment the element exists, not only when the
 * hash changes. Leaving the hash re-arms it; a second arrival lands again.
 *
 * Settings → Email's controls land through it (`#customer-replies` from the ticket composer, and
 * every email banner's link, MAP 249 ticket 07), and so does every addressed `SectionCard`
 * (`/settings#email`, MAP 249 QA D6).
 */
export function useLanding<T extends HTMLElement>(
  anchor: string,
  onLand?: () => void,
): Landing<T> {
  const hash = useRouterState({ select: (s) => s.location.hash });
  const [el, setEl] = useState<T | null>(null);
  const [lit, setLit] = useState(false);
  const landed = useRef(false);
  const onLandRef = useRef(onLand);
  useEffect(() => {
    onLandRef.current = onLand;
  }, [onLand]);

  useEffect(() => {
    if (hash !== anchor) {
      landed.current = false;
      return;
    }
    if (landed.current || !el) return;
    landed.current = true;
    if (typeof el.scrollIntoView === 'function') el.scrollIntoView(LANDING_SCROLL);
    onLandRef.current?.();
    setLit(true);
  }, [hash, anchor, el]);

  useEffect(() => {
    if (!lit) return;
    const id = setTimeout(() => setLit(false), LANDING_HIGHLIGHT_MS);
    return () => clearTimeout(id);
  }, [lit]);

  // ⭐ While it is marked, it STAYS landed: a page opened fresh at the link (the owner's email,
  // a banner on another page) draws the cards ABOVE it after it was scrolled to, and on a wide
  // window the browser's own scroll anchoring did not keep it in view (MAP 249 ticket 08's
  // browser check: 1280 px, pushed ~1,500 px down). So each frame it is put back where the
  // landing left it, until the mark goes or the person scrolls, swipes or presses a key.
  useEffect(() => {
    if (!lit || !el) return;
    if (
      typeof el.getBoundingClientRect !== 'function' ||
      typeof requestAnimationFrame !== 'function' ||
      typeof window === 'undefined' ||
      typeof window.addEventListener !== 'function'
    ) {
      return;
    }
    const at = el.getBoundingClientRect().top;
    let raf = 0;
    let theirs = false;
    const mine = () => {
      theirs = true;
    };
    const events = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;
    for (const e of events) window.addEventListener(e, mine, { passive: true });
    const follow = () => {
      if (theirs) return;
      if (Math.abs(el.getBoundingClientRect().top - at) > 2)
        el.scrollIntoView(LANDING_SCROLL);
      raf = requestAnimationFrame(follow);
    };
    raf = requestAnimationFrame(follow);
    return () => {
      cancelAnimationFrame(raf);
      for (const e of events) window.removeEventListener(e, mine);
    };
  }, [lit, el]);

  return { ref: setEl, lit, mark: lit ? LANDED_MARK : '' };
}
